"use client";

import * as React from "react";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/app/i18n/client";
import { ArrowsTripleLeft, ArrowsTripleRight } from "@/lib/icons";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("relative", className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden">
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      {...props}
    />
  );
});
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof Button>, "variant" | "size">
>(({ className, ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  const { t } = useTranslation();

  return (
    <Button
      ref={ref}
      variant="ghost"
      className={cn(
        "absolute -mx-2 h-fit w-fit px-2",
        orientation === "horizontal"
          ? "-left-16 top-1/2 -translate-y-1/2"
          : "-top-16 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowsTripleLeft className="h-auto max-h-none w-10 max-w-none" />
    </Button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselPreviousSkeleton = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof Button>, "variant" | "size"> & {
    orientation: "horizontal" | "vertical";
  }
>(({ className, orientation, ...props }, ref) => {
  const { t } = useTranslation();

  return (
    <Button
      ref={ref}
      variant="ghost"
      className={cn(
        "absolute -mx-2 h-fit w-fit px-2",
        orientation === "horizontal"
          ? "-left-16 top-1/2 -translate-y-1/2"
          : "-top-16 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      {...props}
    >
      <ArrowsTripleLeft className="h-auto max-h-none w-10 max-w-none" />
    </Button>
  );
});
CarouselPreviousSkeleton.displayName = "CarouselPrevious";

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { api } = useCarousel();
  const [currentSlide, setCurrentSlide] = React.useState(
    api?.selectedScrollSnap() ?? 0,
  );

  React.useEffect(() => {
    if (!api) {
      return;
    }

    const handleScroll = () => {
      setCurrentSlide(api.selectedScrollSnap());
    };

    api.on("scroll", handleScroll);
    api.on("init", handleScroll);

    return () => {
      api.off("scroll", handleScroll);
      api.off("init", handleScroll);
    };
  }, [api]);

  const dots = api?.slideNodes().map((_, index) => (
    <Button
      key={index}
      type="button"
      className={cn(
        "m-0 rounded-full border-2 border-solid border-matchbus bg-transparent p-0 hover:bg-matchbus/80",
        currentSlide === index && "bg-matchbus",
      )}
      onClick={() => api?.scrollTo(index)}
      size="xs"
      variant="secondary"
    >
      <span className="sr-only">Slide {index}</span>
    </Button>
  ));
  return (
    <div
      ref={ref}
      className={cn(
        "absolute -bottom-6 left-1/2 flex -translate-x-1/2 gap-1",
        className,
      )}
      {...props}
    >
      {dots}
    </div>
  );
});
CarouselDots.displayName = "CarouselDots";

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof Button>, "variant" | "size">
>(({ className, ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  const { t } = useTranslation();

  return (
    <Button
      ref={ref}
      variant="ghost"
      className={cn(
        "absolute -mx-2 h-fit w-fit px-2",
        orientation === "horizontal"
          ? "-right-16 top-1/2 -translate-y-1/2"
          : "-bottom-16 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowsTripleRight className="h-auto max-h-none w-10 max-w-none" />
    </Button>
  );
});
CarouselNext.displayName = "CarouselNext";

const CarouselNextSkeleton = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof Button>, "variant" | "size"> & {
    orientation: "horizontal" | "vertical";
  }
>(({ className, orientation, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      variant="ghost"
      className={cn(
        "absolute -mx-2 h-fit w-fit px-2",
        orientation === "horizontal"
          ? "-right-16 top-1/2 -translate-y-1/2"
          : "-bottom-16 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      {...props}
    >
      <ArrowsTripleRight className="h-auto max-h-none w-10 max-w-none" />
    </Button>
  );
});
CarouselNextSkeleton.displayName = "CarouselNext";

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselPreviousSkeleton,
  CarouselNextSkeleton,
  CarouselDots,
};
